
/**
 * Capacitor plugin provides mechanisms for downloading and uploading files.
 * Reference docs used to derive types & behavior:
 *   https://www.npmjs.com/package/@capacitor/file-transfer
 */

import { CapacitorPluginsBase } from "./capacitorPluginsBase";
import moment from "moment";
import * as datadog from "@/shared/datadog";
import { Directory, DownloadFileResult, DownloadFileOptions, ProgressStatus, PluginListenerHandle } from "./types";

class FileHelper extends CapacitorPluginsBase {
  /**
   * Downloads a file in the native device
   * @param args.url - string: a full url of a resource to download.
   * @param args.directory(optional) - Directory: Native file directory where the downloaded file is stored.  Defaults to 'Directory.Documents'.
   * @param args.trackProgress(optional) - boolean: Notify users when the file is downloaded.  Defaults to 'true'.
   * @returns {Promise<DownloadFileResult>} A promise that resolves with the DownloadFileResult with the path.
   */
  async downloadFile (args: { url: string, directory?: Directory, trackProgress?: boolean }): Promise<DownloadFileResult> {
    const directory = args.directory ?? Directory.Documents;
    const trackProgress = args.trackProgress ?? true;
    // fallback file name in case url parsing fails or url doesn't contain a file name
    let fileName = `file_downloaded_${moment().format("MMDDYYYY")}`;

  try {
    const parsedUrl = new URL(args.url);
    const pathname = parsedUrl.pathname;
    // replace spaces with underscores for better compatibility with native file systems
    fileName = pathname.split("/").pop().replace(/%20/g, "_");
  } catch (err) {
    datadog.log("Mobile download url parsing error.", err, "warn");
  }

    // get the file uri for the given directory and file name
    const fileInfo = await this.plugins.Filesystem.getUri({
      directory,
      path: `${fileName}`
    });

    const options: DownloadFileOptions = {
      url: args.url,
      path: fileInfo.uri,
      progress: trackProgress
    };

    return this.downloadWithOptions(options);
  }

  /**
   * Uses the native FileTransfer plugin.
   * Progress events are global; we filter by (type === "download' && url === options.url).
   */
  async downloadWithOptions (
    options: DownloadFileOptions
  ): Promise<DownloadFileResult> {
    let handle: PluginListenerHandle | null = null;
    try {
      if (options.progress) {
        handle = await this.plugins.FileTransfer.addListener(
          "progress",
          this.runOnce(async (evt: ProgressStatus) => {
            if (evt?.type === "download" && evt?.url === options.url) {
              await this.plugins.Toast.show({ text: `File downloaded successfully.`, duration: "long", position: "bottom" });
            }
          })
        );
      }
      await this.plugins.FileTransfer.downloadFile(options);
      return { path: String(options.path) };
    } catch (err) {
      datadog.log(`File download failed. url:${options.url}`, err, "error");
      throw err;
    } finally {
      // clean up progress listener
      if (handle?.remove) {
        try {
          await handle.remove();
        } catch {
          /* ignore */
        }
      }
    }
  }

  private runOnce (fn) {
    let hasRun = false;
    return (...args) => {
      if (!hasRun) {
        hasRun = true;
        fn(...args);
      }
    };
  }
}

export default new FileHelper();